import React, { useCallback, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { tns } from 'tiny-slider/src/tiny-slider';
import { useDataContext } from '../context/DataContext';
import { formatCurrency } from '../lib/helpers';

const skeleton = [];
for (let i = 0; i < 9; i++) {
  skeleton.push(
    <div key={i} className="housing-types-slider__slide housing-types-slider__slide--skeleton">
      <div className="housing-types-slider__slide-inner housing-types-slider__slide-inner--skeleton">
        <figure className="housing-types-slider__visual visual ratio--4x3 housing-type-skeleton" />
        <div className="housing-types-slider__content">
          <div className="housing-types-slider__title">
            <div className="housing-types-slider__subtitle">
              <span className="housing-type-skeleton" style={{width: '40%'}}>&nbsp;</span>
            </div>
          </div>
          <div className="housing-types-slider__price">
            <div className="housing-types-slider__price-prefix">Vanaf</div>
            <div className="custom-checkbox custom-checkbox--block-right">
              <div className="custom-checkbox__element" />
              <span className="housing-type-skeleton" style={{width: '60%'}}>&nbsp;</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Slider = () => {
  const { isLoading, housingTypes, activeFilters, setActiveFilters, isRental } = useDataContext();
  const sliderRef = useRef(null);
  const sliderControlsRef = useRef(null);

  const initSlider = useCallback(() => {
    tns({
      container: sliderRef.current,
      fixedWidth: 220,
      loop: false,
      mouseDrag: true,
      gutter: 24,
      edgePadding: 42,
      nav: false,
      controlsContainer: sliderControlsRef.current
    });
  }, []);

  useEffect(() => {
    if (housingTypes.length && !isLoading) {
      initSlider();
    }
  }, [housingTypes, initSlider, isLoading]);

  const handleChange = (event) => {
    const {checked, name, value} = event.target;

    setActiveFilters((prevState) => ({
      ...prevState,
      [name]: (!checked) ? prevState[name].filter((item) => item !== value) : [...prevState[name], value]
    }));
  };

  const renderPrice = (price) => {
    if (price === '-') return '-';

    const formattedPrice = formatCurrency(price);
    const suffix = isRental ? (<span className="housing-types-slider__price-suffix">per maand</span>) : '';

    if (price > 0) {
      return (
        <>
          {formattedPrice} {suffix}
        </>
      );
    }

    return <span className="text-small">Binnenkort bekend</span>;
  };

  return (
    <>
      <h3 className="housing-types-filter__title sign">Filter op woningtype</h3>
      <div className={`housing-types-slider ${isLoading ? 'housing-types-slider--skeleton' : ''}`}>
        <div ref={sliderRef}>
          {isLoading ? (
            skeleton
          ) : (
            housingTypes.length && housingTypes.map((item) => (
              // eslint-disable-next-line jsx-a11y/label-has-associated-control
              <label key={item.id} className="housing-types-slider__slide">
                <div className={`housing-types-slider__slide-inner ${activeFilters.housingTypes.includes(item.id) ? 'housing-types-slider__slide-inner--active' : ''}`}>
                  <figure className="housing-types-slider__visual visual ratio--4x3">
                    <picture
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.image)
                      }}
                    />
                  </figure>
                  <div className="housing-types-slider__content">
                    <div className="housing-types-slider__title">
                      <div className="housing-types-slider__subtitle">
                        {item.houseCluster}
                      </div>
                    </div>
                    <div className="housing-types-slider__price">
                      <div className="housing-types-slider__price-prefix">{item.price > 0 && <>Vanaf</>}</div>
                      <div className="custom-checkbox custom-checkbox--block-right">
                        <input
                          type="checkbox"
                          name="housingTypes"
                          className="custom-checkbox__input"
                          value={item.id}
                          onChange={handleChange}
                          checked={activeFilters.housingTypes.includes(item.id)}
                        />
                        <div className="custom-checkbox__element" />
                        {renderPrice(item.price)}
                      </div>
                    </div>
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        <div
          className="housing-types-slider__arrows"
          ref={sliderControlsRef}
          aria-label="Slider Navigation"
        >
          <button
            type="button"
            data-controls="prev"
            tabIndex="-1"
            aria-label="Vorige"
          >
            <svg role="img" className="icon">
              <use xlinkHref="/public/img/sprite.svg#arrow-left" />
            </svg>
          </button>
          <button
            type="button"
            data-controls="next"
            tabIndex="-1"
            aria-label="Volgende"
          >
            <svg role="img" className="icon">
              <use xlinkHref="/public/img/sprite.svg#arrow-right" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default Slider;
